import { Component, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NewsletterApiService, ToastService } from '@cnt-workspace/data-access';
import { CinematicRollDirective } from '../directives/cinematic-roll.directive';
import { MagneticBtnDirective } from '../directives/magnetic-btn.directive';

@Component({
  selector: 'curbnturf-footer',
  standalone: true,
  imports: [NgClass, RouterLink, FormsModule, CinematicRollDirective, MagneticBtnDirective],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent {
  private newsletter = inject(NewsletterApiService);
  private toasts = inject(ToastService);

  email = '';
  submitting = false;
  submitted = false;

  /** The copyright year was hardcoded, which quietly goes stale on 77 pages
   *  every January. */
  readonly year = new Date().getFullYear();

  // The property-type and CurbNTurf links are written out in the template
  // rather than driven from arrays here. The original reason was that
  // cntCinematicRoll captured innerHTML in ngOnInit, before Angular resolved
  // {{ }}, so looped labels were destroyed (af2d0870).
  //
  // THAT REASON HAS EXPIRED: the directive now decorates from afterNextRender
  // (moved there so it stops destroying the text node hydration claims), which
  // runs after the first render, so interpolation has resolved by then and a
  // looped label would survive. The remaining constraint is narrower — content
  // must be static AFTER first render, because the directive wipes and rebuilds
  // once and never re-reads. Looping these is now safe; binding something that
  // changes later is not.

  /** Which footer nav sections are expanded on mobile. A Set rather than a
   *  single id so sections open independently — collapsing one to open
   *  another is a worse experience when you are scanning for a link.
   *
   *  This only drives the sub-sm: layout. From sm: up the panels are shown by
   *  a `sm:flex` rule that beats the `hidden` class (media queries come later
   *  in the generated stylesheet), so the 3-column grid never consults state
   *  and the links stay in the DOM at every width — collapsed sections are
   *  display:none, never removed, so they remain crawlable. */
  private readonly openSections = new Set<string>();

  isOpen(id: string): boolean {
    return this.openSections.has(id);
  }

  toggleSection(id: string): void {
    if (!this.openSections.delete(id)) this.openSections.add(id);
  }

  onSubscribe(event: Event): void {
    event.preventDefault();
    if (!this.email || this.submitting) return;
    this.submitting = true;

    this.newsletter.subscribe({ email: this.email }).subscribe({
      next: () => {
        this.submitting = false;
        this.submitted = true;
      },
      error: () => {
        this.submitting = false;
        this.toasts.error('Something went wrong subscribing you. Please try again.');
      },
    });
  }
}
